import type { MaterialInfo, PartDefinition } from "@rogue/rackbuilder"
import { FunctionalTrainerBuilder } from "../../functionalTrainerBuilder"
import { IndyBuilder } from "../indy/indyBuilder"
import { StandardFMBuilder } from "../standard/standardFMBuilder"
import type { RhinoContext } from "./rhinoParts"
import { 
  createRhinoFootplate, 
  createRhinoLatBarClip, 
  getRhinoDepth, 
} from "./rhinoParts"

// RhinoBuilder — adds resistance to back, then optionally delegates sides to IndyBuilder or StandardFMBuilder
export class RhinoBuilder extends FunctionalTrainerBuilder {
  
  // MARK: Main Build Method
  public build(): PartDefinition {
    const baseRack = this.baseRackBuilder.build()

    this.applyStainlessUprights(baseRack)
    this.repositionBandPegsForFT(baseRack)
    this.addFunctionalTrainerComponents(baseRack)
    this.addSideComponentsBasedOnConfig(baseRack)

    return baseRack
  }

  // MARK: Context Factory
  private createContext(materials: MaterialInfo): RhinoContext {
    return {
      config : this.config,
      materials,
      height : this.config.height,
      depth  : getRhinoDepth(this.config),
    }
  }
  
  protected getModelPrefix(): string {
    return 'FM' 
  }
  
  // MARK: Side Components Delegation
  private addSideComponentsBasedOnConfig(baseRack: PartDefinition): void {
    if (this.config.leftSide || this.config.rightSide) {
      this.addIndyComponents(baseRack)
    } else if (this.config.edition === 'rhino trainer' && !this.config.rhinoRackTrainer) {
      this.addFMComponents(baseRack)
    }
  }
  
  private addFMComponents(baseRack: PartDefinition): void {
    const fmBuilder = new StandardFMBuilder(this.config)
  
    // Override: Rhino FXT uses Rhino resistance system, not FM's
    fmBuilder['addResistanceSystem'] = () => {}
  
    const fmMaterials = fmBuilder['materialBuilder'].createMainMaterial()
  
    // Left side
    const leftTopCrossmember = this.getTopCrossmember(baseRack)
    const leftBottomCrossmember = this.getBottomCrossmember(baseRack)
    const leftBackUpright = this.getFarthestBackUpright(baseRack)
  
    fmBuilder.addComponentsToLeftSide(
      fmMaterials,
      leftTopCrossmember,
      leftBottomCrossmember,
      baseRack,
      leftBackUpright
    )
  
    // Right side
    const rightFrontUpright = baseRack.slots['other-side'] as PartDefinition
    const rightTopCrossmember = this.getTopCrossmember(rightFrontUpright)
    const rightBottomCrossmember = this.getBottomCrossmember(rightFrontUpright)
    const rightBackUpright = this.getFarthestBackUpright(rightFrontUpright)
  
    fmBuilder.addComponentsToRightSide(
      fmMaterials,
      rightTopCrossmember,
      rightBottomCrossmember,
      rightFrontUpright,
      rightBackUpright
    )
  }

  private addIndyComponents(baseRack: PartDefinition): void {
    const indyBuilder = new IndyBuilder(this.config)
    indyBuilder['addFunctionalTrainerComponents'](baseRack)
  }
  
  // MARK: Resistance System (Back)
  protected addResistanceSystem(backUpright: PartDefinition, materials: MaterialInfo): void {
    const ctx = this.createContext(materials)
    const latBarClip = createRhinoLatBarClip(this.config)
    const footplate = createRhinoFootplate(ctx, latBarClip)
    
    backUpright.slots['crossmember-bottom'] = footplate
  }
  
  // MARK: Side Components (Rhino-specific)
  protected addSideComponents(
    materials: MaterialInfo,
    topCrossmember: PartDefinition,
    bottomCrossmember: PartDefinition,
    frontUpright: PartDefinition,
    backUpright: PartDefinition,
    left: boolean
  ): void {
    // Rhino only adds resistance system at back (left side only)
    if (left) {
      this.addResistanceSystem(backUpright, materials)
    }
    
    // Add roller if configured (left side only)
    if (this.config.rackRoller && left) {
      const uprightForRoller = this.config.isRhinoHalfRack 
        ? frontUpright 
        : bottomCrossmember.slots.back as PartDefinition
        
      this.addRackRoller(uprightForRoller)
    }
  }
  
  // MARK: Abstract Method Stubs (Rhino doesn't use these)
  protected buildSideMeshes(materials: MaterialInfo, left: boolean): PartDefinition[] {
    return []
  }
  
  protected buildFrontBottomPulley(materials: MaterialInfo, left: boolean): PartDefinition {
    return { mesh: '', slots: {} }
  }
  
  protected attachToHalfRack(
    sideMeshes: PartDefinition[], 
    frontBottomPulley: PartDefinition, 
    topCrossmember: PartDefinition, 
    bottomCrossmember: PartDefinition, 
    left: boolean
  ): void {
    // No-op: Rhino doesn't attach side meshes
  }

  protected attachToFullRack(
    sideMeshes: PartDefinition[], 
    frontBottomPulley: PartDefinition, 
    topCrossmember: PartDefinition, 
    bottomCrossmember: PartDefinition, 
    materials: MaterialInfo, 
    left: boolean
  ): void {
    // No-op: Rhino doesn't attach side meshes
  }
  
  protected addRightSide(
    materials: MaterialInfo, 
    topCrossmember: PartDefinition, 
    bottomCrossmember: PartDefinition, 
    frontUpright: PartDefinition, 
    backUpright: PartDefinition
  ): void {
    // No-op: Rhino handles sides via delegation to FM/Indy builders
  }
}